import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import type { ArrivalCandidate, DealDeadline } from '../../types';
import DeadlineCountdown from './DeadlineCountdown';
import ContractTimelineReview from './ContractTimelineReview';
import { deadlineDate, byDueDate, countedFromWords } from '../../utils/deadlineDates';
import { dueFromEvent } from '../../utils/contractDates';

const SOURCE_WORDS: Record<ArrivalCandidate['arrival_source'], string> = { email: 'by email', filed: 'filed', manual: 'entered by hand' };
const NO_DOCUMENT = '';

/** An event row - its clock starts when a document arrives ("10 days after receipt of the title report"). */
const countsFromEvent = (d: DealDeadline) => d.anchor === 'event' && d.event_days != null;

/**
 * DOCTRACK part 2 (design 4.5): "Received" - when the document that starts this row's clock arrived. Its own action:
 * ⛔ attaching proof marks a row done, which would close the title row instead of starting its objection clock. The due
 * date is counted here, in the browser, by the one date engine (`dueFromEvent`); the server checks and stores it.
 */
function ReceivedForm({ row, onDone, onCancel }: { row: DealDeadline; onDone: () => void; onCancel: () => void }) {
  const [candidates, setCandidates] = useState<ArrivalCandidate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [picked, setPicked] = useState(NO_DOCUMENT);
  const [date, setDate] = useState(row.event_date?.slice(0, 10) ?? '');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.listDeadlineArrivals(row.id)
      .then((r) => {
        if (!live) return;
        const list = r.candidates ?? [];
        setCandidates(list);
        // The document it was recorded with, else the earliest of the kind this row waits for - but a date he typed with no
        // document reopens on "No document", so correcting it never rewrites a document's own arrival.
        const typedAlone = !!row.event_date && !row.filing_id;
        const first = typedAlone ? null
          : list.find((c) => c.filing_id === row.filing_id) ?? list.find((c) => c.filing_id === r.preselected) ?? null;
        if (first) { setPicked(first.filing_id); setDate((d) => d || first.arrival_date); }
      })
      .catch(() => { if (live) setFailed(true); })
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [row.id, row.filing_id]);

  const pick = (filingId: string) => {
    setPicked(filingId);
    const c = candidates.find((x) => x.filing_id === filingId);
    if (c) setDate(c.arrival_date);
  };

  const save = async () => {
    if (!date) return;
    const c = candidates.find((x) => x.filing_id === picked) ?? null;
    // The document's own arrival keeps its source; any other date is one he typed.
    const source = c && c.arrival_date === date ? c.arrival_source : 'manual';
    setBusy(true); setNote(null);
    try {
      const out = await api.recordDeadlineArrival(row.id, { event_date: date, due_date: dueFromEvent(row, date), source, filing_id: c ? c.filing_id : null });
      if (out.kept === 'new') { onDone(); return; }
      const when = deadlineDate(out.deadline?.event_date) ?? '';
      setNote(out.kept === 'typed'
        ? `A date entered by hand is already recorded: ${when} - type a date to change it.`
        : `An earlier arrival is already recorded: ${when} - type a date to change it.`);
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not record the arrival');
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="pl-7 text-xs text-gray-400">Loading documents...</div>;
  return (
    <div className="pl-7 space-y-1.5 text-xs text-gray-600">
      {failed && <p className="text-warning">{"Couldn't load this deal's documents - type the date."}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1">
          Arrived with
          <select aria-label={`Arrived with: ${row.label}`} value={picked} onChange={(e) => pick(e.target.value)} disabled={busy} className="border rounded px-1 py-0.5 text-xs max-w-[20rem]">
            {candidates.map((c) => (
              <option key={c.filing_id} value={c.filing_id}>{`${c.title} - ${deadlineDate(c.arrival_date)}, ${SOURCE_WORDS[c.arrival_source]}`}</option>
            ))}
            <option value={NO_DOCUMENT}>No document - a date typed in</option>
          </select>
        </label>
        <input type="date" aria-label={`Arrival date: ${row.label}`} value={date} onChange={(e) => setDate(e.target.value)} disabled={busy} className="border rounded px-1 py-0.5 text-xs" />
        <button onClick={save} disabled={busy || !date} className="px-2 py-0.5 bg-primary-500 text-white rounded text-xs disabled:opacity-50">Save arrival</button>
        <button onClick={onCancel} disabled={busy} className="text-xs text-gray-400">Cancel</button>
      </div>
      {note && <p className="text-warning">{note}</p>}
    </div>
  );
}

interface Props {
  dealId: string;
  escrowOpenedDate?: string | null;
  onAnchorChanged?: () => void;
}

function EditableDate({ value, onSave }: { value: string | null; onSave: (d: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value?.slice(0, 10) || '');
  if (!editing) {
    return (
      <button onClick={() => { setDraft(value?.slice(0, 10) || ''); setEditing(true); }}
        className="text-xs text-gray-600 hover:text-primary-600" title="Edit date">
        {deadlineDate(value) ?? 'Set date'}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="border rounded px-1 py-0.5 text-xs" />
      <button onClick={() => { if (draft) onSave(draft); setEditing(false); }} className="text-xs text-primary-500">Save</button>
      <button onClick={() => setEditing(false)} className="text-xs text-gray-400">Cancel</button>
    </span>
  );
}

export default function DeadlinesPanel({ dealId, escrowOpenedDate, onAnchorChanged }: Props) {
  const [rows, setRows] = useState<DealDeadline[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState({ label: '', due_date: '' });
  const [anchor, setAnchor] = useState(escrowOpenedDate?.slice(0, 10) || '');
  const [showContract, setShowContract] = useState(false);
  // DOCTRACK part 2: the event row whose "Received" form is open.
  const [receivedFor, setReceivedFor] = useState<string | null>(null);

  useEffect(() => { setAnchor(escrowOpenedDate?.slice(0, 10) || ''); }, [escrowOpenedDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows([...((await api.listDealDeadlines(dealId)).deadlines || [])].sort(byDueDate)); }
    finally { setLoading(false); }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (d: DealDeadline) => { await api.updateDealDeadline(d.id, { is_completed: !d.is_completed }); load(); };
  const setDate = async (d: DealDeadline, due_date: string) => { await api.updateDealDeadline(d.id, { due_date }); load(); };
  const remove = async (d: DealDeadline) => { await api.deleteDealDeadline(d.id); load(); };
  const add = async () => {
    if (!adding.label) return;
    await api.addDealDeadline(dealId, { label: adding.label, due_date: adding.due_date });
    setAdding({ label: '', due_date: '' });
    load();
  };
  const saveAnchor = async () => {
    if (!anchor) return;
    const anchoredCount = rows.filter((r) => r.offset_days != null).length;
    if (!window.confirm(`Set escrow-open date to ${anchor}? This recomputes ${anchoredCount} anchored deadline(s).`)) return;
    await api.setEscrowOpenDate(dealId, anchor);
    await load();
    onAnchorChanged?.();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
        <span className="text-sm text-gray-600 flex-1">Escrow open date (Day 1)</span>
        <input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)} className="border rounded px-2 py-1 text-xs" />
        <button onClick={saveAnchor} disabled={!anchor} className="px-3 py-1 bg-primary-500 text-white rounded text-xs disabled:opacity-50">Set + recompute</button>
        <button onClick={() => setShowContract(true)} className="px-3 py-1 border rounded text-xs">Set up from contract</button>
      </div>

      {loading && rows.length === 0 && <div className="text-sm text-gray-400">Loading deadlines...</div>}
      {!loading && rows.length === 0 && <div className="text-sm text-gray-400">No deadlines yet.</div>}
      {rows.map((d) => {
        // DOCTRACK part 2: an event row says when its document arrived, and - while its date still counts from it - so.
        const arrived = d.event_date ? d.event_date.slice(0, 10) : null;
        const countedFrom = !!arrived && !!d.event_date_source && !!d.due_date && dueFromEvent(d, arrived) === d.due_date.slice(0, 10);
        return (
          <div key={d.id} className="bg-white border rounded-lg px-3 py-2 space-y-1">
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={d.is_completed} onChange={() => toggle(d)} className="rounded" />
              <span className={`flex-1 text-sm ${d.is_completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{d.label}</span>
              {countsFromEvent(d) && (
                <button onClick={() => setReceivedFor((id) => (id === d.id ? null : d.id))} aria-label={`Received: ${d.label}`}
                  className="px-2 py-0.5 border rounded text-xs text-gray-600 hover:text-primary-600">Received</button>
              )}
              <EditableDate value={d.due_date} onSave={(date) => setDate(d, date)} />
              <DeadlineCountdown dueDate={d.due_date} completed={d.is_completed} />
              <button onClick={() => remove(d)} className="text-gray-300 hover:text-red-500 text-xs">x</button>
            </div>
            {arrived && (
              <div className="pl-7 flex flex-wrap gap-x-3 text-[11px] text-gray-500">
                <span>{`${d.event_label ? `${d.event_label} received` : 'Received'}: ${deadlineDate(arrived)}`}</span>
                {countedFrom && <span>{countedFromWords(arrived, d.event_date_source!)}</span>}
              </div>
            )}
            {receivedFor === d.id && (
              <ReceivedForm row={d} onCancel={() => setReceivedFor(null)} onDone={() => { setReceivedFor(null); load(); }} />
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-2 pt-2 border-t">
        <input placeholder="Add deadline..." value={adding.label} onChange={(e) => setAdding((a) => ({ ...a, label: e.target.value }))} className="flex-1 border rounded px-2 py-1 text-sm" />
        <input type="date" value={adding.due_date} onChange={(e) => setAdding((a) => ({ ...a, due_date: e.target.value }))} className="border rounded px-2 py-1 text-xs" />
        <button onClick={add} disabled={!adding.label} className="px-3 py-1 bg-primary-500 text-white rounded text-sm disabled:opacity-50">Add</button>
      </div>
      {showContract && (
        <ContractTimelineReview dealId={dealId} onClose={() => setShowContract(false)} onApplied={() => { load(); onAnchorChanged?.(); }} />
      )}
    </div>
  );
}
